import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Wizard, WizardStep, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { BudgetLegalEntityFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/finance/lib/newBudgetWizard'

import { basicDetailsStep } from './BasicDetails'
import { budgetAccessStep } from './BudgetAccess'
import { budgetAmountsStep } from './BudgetAmounts'
import { budgetLimitsStep } from './BudgetLimits'
import { spendersStep } from './Spenders'

interface Props {
  onSuccess: (budgetId: number) => void
  onError: () => void
  legalEntities: BudgetLegalEntityFragment[]
  has_dev_training_budgets: boolean
}

export const NewBudgetWizard = ({
  onSuccess,
  onError,
  legalEntities,
  has_dev_training_budgets,
}: Props) => {
  const session = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const mainLegalEntity = legalEntities?.find((node) => node.main == true)
  const initialData: WizardData = {
    legalEntityId: mainLegalEntity?.id,
    legalEntities: legalEntities,
    success: false,
    name: '',
    description: '',
    ownerEmployeeIds: [session.employee.id],
    viewerEmployeeIds: [],
    spenderSelection: { type: 'manual', ids: [] },
    effectiveFrom: '',
    amountCents: '',
    currency: mainLegalEntity?.currency ?? '',
    errors: {},
    allocationType: 'none',
    allocatedAmountCents: '',
    budgetableBy: 'expensable',
  }

  const [currentData, setCurrentData] = useState<WizardData>(initialData)

  const handleSubmit = async (data: WizardData) => {
    data.success && data.budgetId ? onSuccess(data.budgetId) : onError()
  }

  // Conditionally include steps based on budgetableBy
  // Memoize steps to avoid recreating on every render, only when budgetableBy changes
  const steps = useMemo(() => {
    // Track budgetableBy changes to conditionally show/hide steps
    const basicDetailsStepWithTracking = (): WizardStep<WizardData> => {
      const originalStep = basicDetailsStep(has_dev_training_budgets)
      return {
        ...originalStep,
        render: (props: WizardStepRenderParams<WizardData>) => {
          const wrappedOnDataChanged = (data: WizardData) => {
            if (data.budgetableBy !== currentData.budgetableBy) {
              setCurrentData(data)
            }
            props.onDataChanged(data)
          }
          return originalStep.render({ ...props, onDataChanged: wrappedOnDataChanged })
        },
      }
    }

    const isTrainingClass = currentData.budgetableBy === 'training_class'

    return [
      basicDetailsStepWithTracking(),
      ...(isTrainingClass
        ? [budgetAmountsStep()]
        : [budgetAccessStep(), budgetLimitsStep(), spendersStep()]),
    ]
  }, [currentData.budgetableBy, has_dev_training_budgets])

  return (
    <Wizard.Modal
      size="large"
      onClose={() => {
        navigate(navigation.expenses.budgets.fullPath())
      }}
      title={i18n.t('budgets.forms.new_budget.title')}
      description={i18n.t('budgets.forms.new_budget.description')}
      icon="money-bag"
      steps={steps}
      initialData={initialData}
      onSubmit={handleSubmit}
    />
  )
}
